import React, { useEffect, useState, Suspense, lazy, createElement, Component } from 'react';
// Error Boundary Component
interface ErrorBoundaryProps {
  children: ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error
    };
  }
  render() {
    if (this.state.hasError) {
      return <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-8" data-id="element-0">
          <div className="max-w-md text-center" data-id="element-1">
            <h1 className="text-2xl font-bold text-[#1f2933] mb-4" data-id="element-2">
              Something went wrong
            </h1>
            <p className="text-[#9ca3af] font-mono text-sm mb-6" data-id="element-3">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button onClick={() => {
            this.setState({
              hasError: false,
              error: null
            });
            window.location.href = '/';
          }} className="px-6 py-3 bg-[#1f2933] text-white rounded-lg font-mono text-sm hover:bg-[#2d3a47] transition-colors" data-id="element-4">
              Return Home
            </button>
          </div>
        </div>;
    }
    return this.props.children;
  }
}
// Loading Fallback
function PageLoader() {
  return <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center" data-id="element-5">
      <div className="text-center" data-id="element-6">
        <div className="w-8 h-8 border-2 border-[#1f2933] border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-7" />
        <p className="font-mono text-xs text-[#9ca3af] tracking-widest" data-id="element-8">
          LOADING...
        </p>
      </div>
    </div>;
}
// Lazy-loaded page imports — only downloaded when the route is visited
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({
  default: m.LandingPage
})));
const InitiateAccessPage = lazy(() => import('./pages/InitiateAccessPage').then(m => ({
  default: m.InitiateAccessPage
})));
const BlueprintPage = lazy(() => import('./pages/BlueprintPage').then(m => ({
  default: m.BlueprintPage
})));
const AffiliateSignupPage = lazy(() => import('./pages/AffiliateSignupPage').then(m => ({
  default: m.AffiliateSignupPage
})));
const CoreCurriculumPage = lazy(() => import('./pages/CoreCurriculumPage').then(m => ({
  default: m.CoreCurriculumPage
})));
const AccountabilityPodsPage = lazy(() => import('./pages/AccountabilityPodsPage').then(m => ({
  default: m.AccountabilityPodsPage
})));
const AIToolsPage = lazy(() => import('./pages/AIToolsPage').then(m => ({
  default: m.AIToolsPage
})));
const ContentFrameworkPage = lazy(() => import('./pages/ContentFrameworkPage').then(m => ({
  default: m.ContentFrameworkPage
})));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({
  default: m.TermsPage
})));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({
  default: m.PrivacyPage
})));
const SitemapPage = lazy(() => import('./pages/SitemapPage').then(m => ({
  default: m.SitemapPage
})));
const AIResearchMasteryPage = lazy(() => import('./pages/AIResearchMasteryPage').then(m => ({
  default: m.AIResearchMasteryPage
})));
const AIResearchBlueprintPage = lazy(() => import('./pages/AIResearchBlueprintPage').then(m => ({
  default: m.AIResearchBlueprintPage
})));
const AIResearchAssessmentPage = lazy(() => import('./pages/AIResearchAssessmentPage').then(m => ({
  default: m.AIResearchAssessmentPage
})));
const DrillingPotPage = lazy(() => import('./pages/DrillingPotPage').then(m => ({
  default: m.DrillingPotPage
})));
const NetworkingSalonExperiencePage = lazy(() => import('./pages/NetworkingSalonExperiencePage').then(m => ({
  default: m.NetworkingSalonExperiencePage
})));
const OnboardPage = lazy(() => import('./pages/OnboardPage').then(m => ({
  default: m.OnboardPage
})));
const TeachersPage = lazy(() => import('./pages/TeachersPage').then(m => ({
  default: m.TeachersPage
})));
export function App() {
  const [currentPage, setCurrentPage] = useState(window.location.pathname);
  const handleNavigate = (path: string) => {
    // Split path and hash
    const [pathname, hash] = path.split('#');
    setCurrentPage(pathname);
    window.history.pushState({}, '', path);
    // Handle scrolling
    if (hash) {
      // Wait for render cycle to complete before scrolling to element
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  };
  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(window.location.pathname);
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  // Load Paystack script on mount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  // Route mapping
  const routes: {
    [key: string]: JSX.Element;
  } = {
    '/': <LandingPage onNavigate={handleNavigate} data-id="element-9" />,
    '/blueprint': <BlueprintPage onNavigate={handleNavigate} data-id="element-10" />,
    '/join': <InitiateAccessPage onNavigate={handleNavigate} data-id="element-11" />,
    '/initiate-access': <InitiateAccessPage onNavigate={handleNavigate} data-id="element-12" />,
    '/affiliate-signup': <AffiliateSignupPage onNavigate={handleNavigate} data-id="element-13" />,
    '/affiliate': <AffiliateSignupPage onNavigate={handleNavigate} data-id="element-14" />,
    '/core-curriculum': <CoreCurriculumPage onNavigate={handleNavigate} data-id="element-15" />,
    '/curriculum': <CoreCurriculumPage onNavigate={handleNavigate} data-id="element-16" />,
    '/accountability-pods': <AccountabilityPodsPage onNavigate={handleNavigate} data-id="element-17" />,
    '/pods': <AccountabilityPodsPage onNavigate={handleNavigate} data-id="element-18" />,
    '/ai-tools': <AIToolsPage onNavigate={handleNavigate} data-id="element-19" />,
    '/ai': <AIToolsPage onNavigate={handleNavigate} data-id="element-20" />,
    '/content-framework': <ContentFrameworkPage onNavigate={handleNavigate} data-id="element-21" />,
    '/framework': <ContentFrameworkPage onNavigate={handleNavigate} data-id="element-22" />,
    '/terms': <TermsPage onNavigate={handleNavigate} data-id="element-23" />,
    '/privacy': <PrivacyPage onNavigate={handleNavigate} data-id="element-24" />,
    '/sitemap': <SitemapPage onNavigate={handleNavigate} data-id="element-25" />,
    '/pages': <SitemapPage onNavigate={handleNavigate} data-id="element-26" />,
    '/ai-research-mastery': <AIResearchMasteryPage onNavigate={handleNavigate} data-id="element-27" />,
    '/ai-research-blueprint': <AIResearchBlueprintPage onNavigate={handleNavigate} data-id="element-28" />,
    '/ai-research-assessment': <AIResearchAssessmentPage onNavigate={handleNavigate} data-id="element-29" />,
    '/lounge': <AIResearchAssessmentPage onNavigate={handleNavigate} data-id="element-30" />,
    '/drilling-pot': <DrillingPotPage onNavigate={handleNavigate} data-id="element-31" />,
    '/networking-salon-experience': <NetworkingSalonExperiencePage onNavigate={handleNavigate} data-id="element-32" />,
    '/salon': <NetworkingSalonExperiencePage onNavigate={handleNavigate} data-id="element-33" />,
    '/onboard': <OnboardPage onNavigate={handleNavigate} data-id="element-34" />,
    '/teachers': <TeachersPage onNavigate={handleNavigate} data-id="element-35" />
  };
  // Get the current page component or show 404
  const currentComponent = routes[currentPage] || routes['/'];
  return <ErrorBoundary data-id="element-36">
      <Suspense fallback={<PageLoader data-id="element-38" />} data-id="element-37">{currentComponent}</Suspense>
    </ErrorBoundary>;
}